import { useRouter } from 'next/router';
import { IPageModel, IReturnTypeOFTargetMboxParam } from '../types';
import { DataLayerEvent, useSession } from '../lib/session';
import { useEffect, useState } from 'react';
import { isEmpty } from '@marriott/shared/mi-helper-utils';
import { useClientEnvVarsStore, useDCAPageModelStore } from '@marriott/mi-store-utils';
import { targetPayloadRequest } from '../lib/dynamicContentAssemblyUtils/targetSDKPayload';
import { fetchDCADynamicModelForAlloy } from './useDCADynamicModel';
import { getMboxParamters } from '../lib/helpers';
import { IAlloyCallResponse, Proposition } from '../abTestAlloyResponseType';
import { Alloy } from './use-alloy';

export const chaseCodes = ['updateboundless', 'updatebold', 'updatecompboundless', 'updatecompbold'];
export const amexCodes = [
  'updatebrilliant',
  'updatebusiness',
  'updatebevy',
  'updatecompbrilliant',
  'updatecompbusiness',
  'updatecompbevy',
];

declare global {
  interface Window {
    alloy: Alloy;
  }
}

export const useDCAAlloyModel = (targetMboxes: IReturnTypeOFTargetMboxParam[]) => {
  const router = useRouter();
  const queryParams = router?.query;
  const DST_VALUES = [...amexCodes, ...chaseCodes];
  const isDSTvalue = DST_VALUES.some(key =>
    Object.keys(queryParams).some(param => param.toLowerCase() === key?.toLowerCase())
  );
  const { datalayer, mvpOffers, updateDatalayer } = useSession();
  const [isCallTriggered, setIsCallTriggered] = useState(false);
  const updatePageModel = useDCAPageModelStore(state => state.setPageModel);

  const updateComponentWithDefaultContent = (listOfTargetMboxes: string[]) => {
    /**
     * After timeout, if launchLoaded event hasn't fired, update pageModel with null values
     * for each target mbox so that components will set isTargetContentLoaded:true
     * and stop showing skeleton loaders. This handles cases where Adobe Launch is blocked
     * or when adobeDataLayer exists but the launchLoaded event never fires.
     * Only set null for mboxes that don't already have data to avoid overwriting successful responses.
     */
    const prevPageModel = useDCAPageModelStore?.getState()?.pageModel as Record<string, unknown>;
    const prevAlloyModelJSON = (prevPageModel?.['alloyModelJSON'] || {}) as Record<string, unknown>;
    const updatedTargetMboxWithNullValues = listOfTargetMboxes?.reduce((mboxes: Record<string, unknown>, item) => {
      // Only set to null if the mbox doesn't already have data
      if (prevAlloyModelJSON[item] == undefined) {
        mboxes[item] = null;
      }
      return mboxes;
    }, {});

    /**
     * this check is added just to ensure, if we got data and all mboxes are also updated then there is
     * no need to update this pageModel, thats checking for keys length
     * if its zero means all are updated then no need to trigger this state
     */
    if (Object.keys(updatedTargetMboxWithNullValues)?.length) {
      updatePageModel({
        ...(prevPageModel ? prevPageModel : {}),
        alloyModelJSON: {
          ...prevAlloyModelJSON,
          ...updatedTargetMboxWithNullValues,
        },
      });
    }
  };

  useEffect(() => {
    if (!isEmpty(datalayer) && !isCallTriggered) {
      setIsCallTriggered(true);
      const listOfTargetMboxes = targetMboxes?.map(item => item.targetMbox);
      if (listOfTargetMboxes?.length > 0 && !isDSTvalue) {
        launchLoadedAddEventListener(listOfTargetMboxes);
      } else if (isDSTvalue && listOfTargetMboxes?.length) {
        updateComponentWithDefaultContent(listOfTargetMboxes);
      }
    }
  }, [datalayer]);

  const launchLoadedAddEventListener = (listOfTargetMboxes: string[]) => {
    const handleLaunchLoaded = () => {
      fetchTargetData(listOfTargetMboxes)?.then(() => {
        const updatedAlloyComponentWithoutMbox: Record<string, unknown> = {},
          alloyComponentsPageModel: Record<string, unknown> = {};

        targetMboxes.forEach(alloyItem => {
          if (alloyItem.alloyResponse) {
            alloyComponentsPageModel[alloyItem.targetMbox] = { ...alloyItem.alloyResponse };
            const { mboxParameters: list } = getMboxParamters(alloyItem.alloyResponse as unknown as IPageModel);
            if (list.length == 0) {
              updatedAlloyComponentWithoutMbox[alloyItem.targetMbox] = alloyItem.alloyResponse;
            }
          } else if (alloyItem.targetMbox && !alloyItem.alloyResponse) {
            alloyComponentsPageModel[alloyItem.targetMbox] = null;
          }
        });
        if (!isEmpty(updatedAlloyComponentWithoutMbox)) {
          const prevPageModel = useDCAPageModelStore?.getState()?.pageModel as Record<string, Record<string, unknown>>;
          const prevAlloyModelJSON = prevPageModel?.['alloyModelJSON'];

          updatePageModel({
            ...(prevPageModel ? prevPageModel : {}),
            alloyModelJSON: {
              ...prevAlloyModelJSON,
              ...updatedAlloyComponentWithoutMbox,
            },
          });
        }
        const { mboxParameters } = getMboxParamters(alloyComponentsPageModel as unknown as IPageModel);
        fetchDCADynamicModelForAlloy(
          mboxParameters,
          alloyComponentsPageModel as unknown as IPageModel,
          datalayer as Record<string, unknown>,
          mvpOffers
        )?.then(response => {
          const prevPageModel = useDCAPageModelStore?.getState()?.pageModel as Record<string, unknown>;
          const prevAlloyModelJSON = prevPageModel?.['alloyModelJSON'] || {};

          updatePageModel({
            ...(prevPageModel ? prevPageModel : {}),
            alloyModelJSON: {
              ...prevAlloyModelJSON,
              ...(isEmpty(response) ? alloyComponentsPageModel : response),
            },
          });
        });
      });
    };

    if (window !== undefined && window.adobeDataLayer) {
      window?.adobeDataLayer?.addEventListener('launchLoaded', handleLaunchLoaded);
    }

    const timeoutId = setTimeout(() => {
      if (window !== undefined) {
        window?.adobeDataLayer?.removeEventListener('launchLoaded', handleLaunchLoaded);
        updateComponentWithDefaultContent(listOfTargetMboxes);
      }
    }, 10000);

    return () => {
      clearTimeout(timeoutId);
      if (window !== undefined && window.adobeDataLayer) {
        window?.adobeDataLayer?.removeEventListener('launchLoaded', handleLaunchLoaded);
      }
    };
  };

  const fetchTargetData = async (listOfTargetMboxes?: string[]) => {
    const payLoad = targetPayloadRequest(
      {
        isUxl: 'false',
        locale:
          window?.sessionData?.locale?.replace('-', '_') ||
          window?._satellite?.['getVar']('brwsrLang') ||
          datalayer?.['brwsrLang'] ||
          'en_US',
        mboxName: listOfTargetMboxes,
        propertyToken:
          process.env['TARGET_PROPERTY_TOKEN'] ||
          useClientEnvVarsStore.getState().envVarsObject['TARGET_PROPERTY_TOKEN'],
      },
      datalayer,
      mvpOffers
    );
    try {
      /**
       * If alloy call is always in pending state, then for that case added this setTimout inside Promise.race
       * so that it will throw error and component state also updated
       */
      const result = await Promise.race<IAlloyCallResponse>([
        window?.alloy<IAlloyCallResponse>('sendEvent', {
          data: {
            __adobe: {
              target: payLoad?.execute?.mboxes?.[0]?.parameters,
            },
          },
          xdm: {},
          decisionScopes: listOfTargetMboxes,
        }),
        new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error('Timeout'));
          }, 7000);
        }),
      ]);

      const propositions = result?.propositions;
      let item: string;
      const experience: string[] = [];
      let foundProposition = false;
      const propositionItems: Proposition[] = [];

      if (propositions && targetMboxes?.length) {
        for (const proposition of propositions) {
          item = proposition?.scope;
          propositionItems.push(proposition);
          foundProposition = true;

          if (item) {
            targetMboxes?.forEach(element => {
              if (element.targetMbox === item) {
                const targetObject =
                  proposition?.items?.[0].data?.content?.[':items'].root?.[':items']?.responsivegrid?.[':items'];
                const componentName = targetObject ? Object.keys(targetObject)?.[0] : '';
                element.alloyResponse = targetObject?.[componentName] as unknown as Record<string, unknown>;
              }
            });
          }
        }

        if (foundProposition) {
          updateDatalayer({
            event: 'card-layout-updated',
            data: {
              items: propositionItems,
              experiences: experience,
              targetData: targetMboxes?.map(item => ({
                [item.targetMbox]: item.alloyResponse,
              })),
            },
            skipDatalayerUpdate: true,
          } as unknown as DataLayerEvent);
        }
      }

      if (propositionItems.length) {
        window?.alloy('sendEvent', {
          xdm: {
            eventType: 'decisioning.propositionDisplay',
            _experience: {
              decisioning: {
                propositions: propositionItems?.map(item => ({
                  id: item?.id,
                  scope: item?.scope,
                  scopeDetails: item?.scopeDetails,
                })),
                propositionEventType: {
                  display: 1,
                },
              },
            },
          },
        });
      }
    } catch (error) {
      console.log('Error occurred in Target call', error);
    }
  };
};
